import { FC, useCallback } from 'react';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import SelectCompany from 'src/components/SelectCompany';
import UploadFilesModal from 'src/components/UploadFilesModal';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import useBoolean from 'src/hooks/useBoolean';
import useCompany from 'src/hooks/useCompany';
import useConfirm from 'src/hooks/useConfirm';
import useScanManager from 'src/hooks/useScanManager';
import useDeleteScanManagerInvoicesApi from 'src/services/useDeleteScanManagerInvoices';
import useDeleteScanManagerReceiptsApi from 'src/services/useDeleteScanManagerReceiptsApi';

interface DropDownProps {}

const ScanManagerHeader: FC<DropDownProps> = () => {
  const {
    count,
    showPrev,
    isInvoices,
    setShowPrev,
    attachmentListData,
    getAttachDocuments,
    refreshCompany,
    setRefreshCompany,
    selectedDocuments,
  } = useScanManager();
  const { company: selectedCompany } = useCompany();
  const { value: isUploadOpen, setTrue: openUpload, setFalse: closeUpload } = useBoolean();
  const { confirm } = useConfirm();

  const { deletePendingInvoiceAttachments, isDeleteInvoiceAttachmentsLoading } =
    useDeleteScanManagerInvoicesApi();
  const { deletePendingReceiptAttachments, isDeleteReceiptAttachmentsLoading } =
    useDeleteScanManagerReceiptsApi();

  const deleteItem = useCallback(async () => {
    const selectedIdArr = attachmentListData
      .filter((row) => row.selected)
      .map((selectedRow) => selectedRow.attachId);
    isInvoices
      ? await deletePendingInvoiceAttachments({ attachIds: selectedIdArr }).then(() => {
          getAttachDocuments();
        })
      : await deletePendingReceiptAttachments({ attachIds: selectedIdArr }).then(() => {
          getAttachDocuments();
        });
  }, [
    attachmentListData,
    deletePendingInvoiceAttachments,
    deletePendingReceiptAttachments,
    getAttachDocuments,
    isInvoices,
  ]);

  const handleDelete = useCallback(async () => {
    const isDeleteAccepted = await confirm({
      title: 'Delete Document(s)',
      content: <div>Are you sure you want to Delete the selected document(s)?</div>,
      confirmButtonText: 'OK',
    });
    if (isDeleteAccepted) {
      await deleteItem();
      setRefreshCompany?.((prev) => !prev);
    }
  }, [confirm, deleteItem, setRefreshCompany]);

  return (
    <>
      {isUploadOpen && (
        <UploadFilesModal
          open={isUploadOpen}
          closeModal={closeUpload}
          refreshParent={getAttachDocuments}
          isInvoice={isInvoices}
          companyId={parseInt(selectedCompany?.companyId)}
          setRefreshCompany={setRefreshCompany}
        />
      )}

      <nav className="flex items-center justify-between flex-wrap">
        <div className="md:w-1/3 w-full">
          <SelectCompany
            isInvoice={isInvoices}
            isScanManager={true}
            refreshCompany={refreshCompany}
          />
        </div>
        <div className="flex items-center md:justify-center flex-wrap">
          {selectedCompany?.companyId && (
            <div className="flex-grow flex justify-center">
              <Button color="primary" onClick={openUpload}>
                Upload Document
              </Button>

              <PopUp
                content={
                  <div className="m-1 max-w-[300px]">
                    <PopUpContentWrapper
                      type="Information"
                      contents={['Please select one or more rows to delete']}
                    />
                  </div>
                }
                show={!selectedDocuments.length}
              >
                <Button
                  color="error"
                  onClick={handleDelete}
                  disabled={!selectedDocuments.length}
                  loading={
                    isInvoices
                      ? isDeleteInvoiceAttachmentsLoading
                      : isDeleteReceiptAttachmentsLoading
                  }
                >
                  Delete Document
                </Button>
              </PopUp>
            </div>
          )}
        </div>
        <div className="md:w-1/3 w-full flex justify-end">
          {selectedCompany?.companyId && (
            <Button
              onClick={() => setShowPrev((prev) => !prev)}
              endIcon={
                <div className="bg-dark rounded-full h-5 w-5 flex items-center justify-center text-[0.6rem] text-white">
                  {count || 0}
                </div>
              }
              color="light"
            >
              {!showPrev ? 'Show Scans' : 'Hide Scans'}
            </Button>
          )}
        </div>
      </nav>
    </>
  );
};

export default ScanManagerHeader;
